/**
 * SecurityAuditLog Page — Immutable audit trail viewer (B.9)
 *
 * Read-only — no edit/delete UI.
 * Filterable by actor, action, target type, date range.
 * Paginated at 50 entries per page.
 */

import { useState } from 'react';
import { useAuditLog } from '../hooks/useSecurity';
import { ScrollText, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const ACTION_LABELS: Record<string, string> = {
  session_terminate: 'Session Terminate',
  account_lock: 'Account Lock',
  account_unlock: 'Account Unlock',
  alert_acknowledge: 'Alert Acknowledge',
  alert_resolve: 'Alert Resolve',
  alert_dismiss: 'Alert Dismiss',
  password_reset: 'Password Reset',
  password_change: 'Password Change',
  mfa_enable: 'MFA Enable',
  mfa_disable: 'MFA Disable',
  user_create: 'User Create',
  user_update: 'User Update',
  user_deactivate: 'User Deactivate',
  site_access_update: 'Site Access Update',
  lockout_policy_update: 'Lockout Policy Update',
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function SecurityAuditLog() {
  const [actorFilter, setActorFilter] = useState('');
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [targetTypeFilter, setTargetTypeFilter] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const limit = 50;

  const { data, isLoading, isError } = useAuditLog({
    actor: actorFilter || undefined,
    action: actionFilter,
    target_type: targetTypeFilter,
    limit,
    offset: page * limit,
  });

  const entries = data?.entries || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center gap-3">
        <ScrollText className="w-5 h-5 text-ems-green" />
        <div>
          <h1 className="text-lg font-bold text-ems-text-primary">Security Audit Log</h1>
          <p className="text-sm text-ems-text-muted">Immutable record of all administrative actions.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Filter by actor email..."
            value={actorFilter}
            onChange={(e) => { setActorFilter(e.target.value); setPage(0); }}
            className="text-xs border border-ems-border rounded-lg px-3 py-1.5 w-48 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20"
          />
          <select
            value={actionFilter || ''}
            onChange={(e) => { setActionFilter(e.target.value || undefined); setPage(0); }}
            className="text-xs border border-ems-border rounded-lg px-3 py-1.5 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20"
          >
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <select
            value={targetTypeFilter || ''}
            onChange={(e) => { setTargetTypeFilter(e.target.value || undefined); setPage(0); }}
            className="text-xs border border-ems-border rounded-lg px-3 py-1.5 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20"
          >
            <option value="">All target types</option>
            <option value="user">User</option>
            <option value="session">Session</option>
            <option value="alert">Alert</option>
            <option value="account">Account</option>
            <option value="site_access">Site Access</option>
          </select>
          <span className="text-xs text-ems-text-muted ml-auto">
            {total} entries
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
          </div>
        ) : isError ? (
          <p className="text-sm text-ems-red py-8 text-center">Failed to load audit log.</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-ems-text-muted py-8 text-center">No audit entries found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ems-text-muted border-b border-ems-border bg-ems-bg-card-hover">
                  <th className="px-4 py-2.5 font-medium">Timestamp</th>
                  <th className="px-4 py-2.5 font-medium">Actor</th>
                  <th className="px-4 py-2.5 font-medium">Action</th>
                  <th className="px-4 py-2.5 font-medium">Target</th>
                  <th className="px-4 py-2.5 font-medium">Target ID</th>
                  <th className="px-4 py-2.5 font-medium">IP</th>
                  <th className="px-4 py-2.5 font-medium w-8"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isExpanded = expandedId === entry.log_id;
                  return (
                    <>
                      <tr key={entry.log_id} className="border-b border-ems-border hover:bg-ems-bg-card-hover">
                        <td className="px-4 py-2 text-ems-text-muted whitespace-nowrap">
                          {formatTime(entry.created_at)}
                        </td>
                        <td className="px-4 py-2 font-medium text-ems-text-primary">{entry.actor_email}</td>
                        <td className="px-4 py-2">
                          <span className="px-1.5 py-0.5 rounded bg-ems-bg-card-hover text-ems-text-primary text-[10px] font-medium">
                            {ACTION_LABELS[entry.action] || entry.action}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-ems-text-muted">{entry.target_type}</td>
                        <td className="px-4 py-2 font-mono text-ems-text-muted text-[10px]">
                          {entry.target_id.length > 12
                            ? `${entry.target_id.slice(0, 8)}...`
                            : entry.target_id}
                        </td>
                        <td className="px-4 py-2 font-mono text-ems-text-muted">{entry.source_ip || '—'}</td>
                        <td className="px-4 py-2">
                          {entry.details && (
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : entry.log_id)}
                              className="p-0.5 rounded hover:bg-ems-bg-card-hover"
                            >
                              {isExpanded
                                ? <ChevronUp className="w-3.5 h-3.5 text-ems-text-muted" />
                                : <ChevronDown className="w-3.5 h-3.5 text-ems-text-muted" />}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && entry.details && (
                        <tr key={`${entry.log_id}-detail`}>
                          <td colSpan={7} className="px-4 py-2 bg-ems-bg">
                            <pre className="text-[10px] text-ems-text-muted whitespace-pre-wrap font-mono">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ems-border">
            <span className="text-xs text-ems-text-muted">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
